import { useState } from 'react';

export const HIGH_VOLUME_THRESHOLD = 200;

/**
 * CohortPreviewSummary
 *
 * Muestra el breakdown del bulk enrollment antes de confirmar.
 *
 * Aritmética: total = ready + (skipped.completed + skipped.sold + skipped.already_in_cohort).
 * El warning_active_cohort_count es un SUBSET de ready (no es un grupo separado).
 *
 * Props:
 * - stats: response del endpoint /cohorts/preview-bulk:
 *   {
 *     total, ready,
 *     skipped: { completed, sold, already_in_cohort },
 *     warning_active_cohort_count,
 *     warning_active_cohort_breakdown: [{ campaign_id, campaign_name, count }]
 *   }
 */
export default function CohortPreviewSummary({ stats }) {
  const [expandedWarning, setExpandedWarning] = useState(false);

  if (!stats) return null;

  const isHighVolume = stats.total >= HIGH_VOLUME_THRESHOLD;
  const totalSkipped =
    (stats.skipped?.completed || 0) +
    (stats.skipped?.sold || 0) +
    (stats.skipped?.already_in_cohort || 0);

  return (
    <div className="space-y-3 text-sm">
      <div className="text-gray-700 dark:text-gray-200">
        👥 <strong>{stats.total}</strong> contactos
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
        <div className="text-green-700 dark:text-green-400 font-medium">
          ✅ <strong>{stats.ready}</strong> SE INSCRIBIRÁN
        </div>

        {totalSkipped > 0 && (
          <div className="text-red-700 dark:text-red-400">
            <div className="font-medium mb-1">
              ❌ {totalSkipped} NO entrarán a esta cohorte:
            </div>
            <ul className="ml-6 space-y-0.5 text-xs text-red-600 dark:text-red-300">
              {stats.skipped.completed > 0 && (
                <li>└ {stats.skipped.completed} ya completaron esta campaña</li>
              )}
              {stats.skipped.sold > 0 && (
                <li>└ {stats.skipped.sold} son clientes vendidos</li>
              )}
              {stats.skipped.already_in_cohort > 0 && (
                <li>└ {stats.skipped.already_in_cohort} ya están en esta cohorte (duplicados)</li>
              )}
            </ul>
          </div>
        )}

        {stats.warning_active_cohort_count > 0 && (
          <div className="text-amber-700 dark:text-amber-400">
            <div className="font-medium mb-1">
              ⚠️ De los {stats.ready} que SE INSCRIBIRÁN, {stats.warning_active_cohort_count} también
              están en otra cohorte activa
              <button
                type="button"
                onClick={() => setExpandedWarning(v => !v)}
                className="ml-2 text-xs underline hover:no-underline"
              >
                {expandedWarning ? 'ocultar detalle ▴' : 'ver detalle ▾'}
              </button>
            </div>
            {expandedWarning && (
              <ul className="ml-6 space-y-0.5 text-xs text-amber-600 dark:text-amber-300">
                {(stats.warning_active_cohort_breakdown || []).map((b) => (
                  <li key={b.campaign_id}>└ "{b.campaign_name}" ({b.count})</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {stats.ready > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-900 dark:text-blue-200">
          <div className="flex items-start gap-2">
            <span className="text-blue-700 dark:text-blue-300 text-base shrink-0">⏱</span>
            <div>
              <div className="font-medium mb-1">Tiempo estimado de envío del paso 1:</div>
              <div className="text-blue-700 dark:text-blue-300 font-semibold">
                ~{stats.ready} minuto{stats.ready !== 1 ? 's' : ''} para {stats.ready} contacto{stats.ready !== 1 ? 's' : ''}
                <span className="text-blue-500 dark:text-blue-400 text-[10px] ml-2 font-normal">
                  (1 mensaje cada 60 segundos)
                </span>
              </div>
              <div className="text-blue-600 dark:text-blue-400 text-[10px] mt-1">
                Los envíos empiezan al iniciar la cohorte (tras lock window de 30 min).
              </div>
            </div>
          </div>
        </div>
      )}

      {isHighVolume && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 text-xs text-amber-700 dark:text-amber-400">
          ⚠️ Estás por inscribir más de {HIGH_VOLUME_THRESHOLD} contactos.
        </div>
      )}
    </div>
  );
}
